/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import {
  findContentControlsByTag,
  insertContentControl,
  stampAllParagraphs,
} from '@ecsper/host-word';
import { v7 as uuidv7 } from 'uuid';

import {
  CPR_PART_35_DECLARATION_TEXT,
  DECLARATION_TAG,
  SOT_TAG_PREFIX,
  STATEMENT_OF_TRUTH_TEXT,
} from '../content-controls.js';

/**
 * Insert a Statement of Truth content control at the current selection,
 * pre-populated with the standard text, then lock it.
 * If a Statement of Truth already exists in the document, return that
 * one without inserting a duplicate (per CPR convention).
 */
export async function insertStatementOfTruth(): Promise<{
  id: number;
  tag: string;
}> {
  const existing = await findContentControlsByTag(SOT_TAG_PREFIX);
  if (existing.length > 0) {
    return { id: existing[0]!.id, tag: existing[0]!.tag };
  }
  const tag = `${SOT_TAG_PREFIX}${uuidv7()}`;
  const ref = await insertContentControl({
    tag,
    title: 'Statement of Truth',
    content: STATEMENT_OF_TRUTH_TEXT,
    locked: true,
  });
  return { id: ref.id, tag: ref.tag };
}

/**
 * Insert the CPR Part 35 declaration content control.
 * Singleton per document. Returns existing if already present.
 */
export async function insertCprDeclaration(): Promise<{
  id: number;
  tag: string;
}> {
  const existing = await findContentControlsByTag(DECLARATION_TAG);
  if (existing.length > 0) {
    return { id: existing[0]!.id, tag: existing[0]!.tag };
  }
  const ref = await insertContentControl({
    tag: DECLARATION_TAG,
    title: 'CPR Part 35 Declaration',
    content: CPR_PART_35_DECLARATION_TEXT,
    locked: true,
  });
  return { id: ref.id, tag: ref.tag };
}

/**
 * Stamp all unstamped paragraphs with UUID v7. Returns the count
 * of paragraphs stamped (new entries) for telemetry.
 */
export async function stampDocumentParagraphs(): Promise<{
  totalParagraphs: number;
}> {
  const registry = await stampAllParagraphs();
  return { totalParagraphs: registry.length };
}
